import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success('Reset code sent', { description: 'Check your phone or email for the code.' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="pt-32 pb-24 min-h-screen flex items-center" data-testid="forgot-password-sent">
        <div className="chaleur-container">
          <div className="max-w-md mx-auto text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-50 flex items-center justify-center">
              <Mail className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-3xl font-light mb-4">Check Your Messages</h1>
            <p className="text-muted-foreground mb-8">
              If an account exists for <strong>{email}</strong>, we've sent a 6-digit reset code to your registered phone number.
            </p>
            <Button
              className="btn-chaleur-primary w-full"
              onClick={() => navigate(`/reset-password?email=${encodeURIComponent(email)}`)}
              data-testid="enter-code-btn"
            >
              Enter Reset Code
            </Button>
            <button
              onClick={() => setSent(false)}
              className="mt-4 text-sm text-muted-foreground hover:text-foreground underline"
            >
              Didn't receive it? Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 min-h-screen flex items-center" data-testid="forgot-password-page">
      <div className="chaleur-container">
        <div className="max-w-md mx-auto">
          <Link to="/login" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Sign In
          </Link>

          <div className="text-center mb-12">
            <h1 className="text-3xl font-light mb-4">Reset Password</h1>
            <p className="text-muted-foreground">Enter your email and we'll send you a reset code</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-sm mb-2 block">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-chaleur"
                required
                placeholder="your@email.com"
                data-testid="forgot-email"
              />
            </div>

            <Button type="submit" className="btn-chaleur-primary w-full" disabled={loading} data-testid="send-reset-btn">
              {loading ? 'Sending...' : 'Send Reset Code'}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground">
              Remember your password?{' '}
              <Link to="/login" className="text-primary hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
